import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject, signal, computed } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { DocumentSummary, PaginatedDocumentResponse } from '@shared/models/dto/pagination';
import { UserAccessService } from './user-access.service';
import { DocumentChangeDetectionService } from './document-change-detection.service';

export interface PaginationState {
  page?: number;
  first: number;
  rows: number;
}

@Injectable({
  providedIn: 'root'
})
export class DocumentSearchService {
  private http = inject(HttpClient);
  private userAccess = inject(UserAccessService);
  private changeDetection = inject(DocumentChangeDetectionService);
  private apiUrl = environment.backendHost;

  // Documents state
  private _documents = signal<DocumentSummary[]>([]);
  private _totalDocuments = signal<number>(0);

  // Filters
  private _searchText = signal<string>('');
  private _statusFilter = signal<string>('processed');
  private _sortField = signal<string>('');
  private _sortAscending = signal<boolean>(true);

  // Pagination
  private _currentPage = signal<number>(0);
  private _rowsPerPage = signal<number>(10);
  private _paginationResetTrigger = signal<number>(0);

  // Loading and error states
  private _isLoading = signal<boolean>(false);
  private _error = signal<string | null>(null);

  // Public computed signals
  readonly documents = computed(() => this._documents());
  readonly totalDocuments = computed(() => this._totalDocuments());
  readonly searchText = computed(() => this._searchText());
  readonly statusFilter = computed(() => this._statusFilter());
  readonly sortField = computed(() => this._sortField());
  readonly sortAscending = computed(() => this._sortAscending());
  readonly currentPage = computed(() => this._currentPage());
  readonly rowsPerPage = computed(() => this._rowsPerPage());
  readonly isLoading = computed(() => this._isLoading());
  readonly error = computed(() => this._error());
  readonly paginationResetTrigger = computed(() => this._paginationResetTrigger());

  // Pagination info
  readonly isFirstPage = computed(() => this._currentPage() === 0);
  readonly isLastPage = computed(() => {
    const page = this._currentPage();
    const rows = this._rowsPerPage();
    const total = this._totalDocuments();
    return (page + 1) * rows >= total;
  });

  constructor() {
    // NO effect here - triggers are manual only
  }

  /**
   * Search documents with current filters and pagination
   */
  searchDocuments(paginationState: PaginationState, forceRefresh: boolean = true): void {
    if (!this.userAccess.hasValidSelection()) {
      console.warn('[DocumentSearchService] No valid selection, skipping search');
      return;
    }

    console.log('[DocumentSearchService] searchDocuments called:', paginationState);

    if (forceRefresh) {
      this._documents.set([]);
    }

    this._isLoading.set(true);
    this._error.set(null);

    const page = paginationState.page !== undefined 
      ? paginationState.page 
      : Math.floor(paginationState.first / paginationState.rows);
    
    this._currentPage.set(page);
    this._rowsPerPage.set(paginationState.rows);

    const params = this.buildSearchParams(paginationState);

    this.http.get<PaginatedDocumentResponse>(
      `${this.apiUrl}/api/v1/document/search/documents`,
      { params }
    ).subscribe({
      next: (response) => {
        console.log('[DocumentSearchService] Response received:', response);
        this.handleSearchResponse(response);
      },
      error: (err) => {
        this._error.set('Failed to load documents. Please try again.');
        this._isLoading.set(false);
        console.error('[DocumentSearchService] Search error:', err);
      }
    });
  }

  /**
   * Build HTTP params for search request
   */
  private buildSearchParams(paginationState: PaginationState): HttpParams {
    const { libraryId, classId } = this.userAccess.currentSelection();
    
    let params = new HttpParams()
      .set('libraryId', libraryId)    
      .set('classId', classId)   
      .set('offset', paginationState.first.toString())
      .set('limit', paginationState.rows.toString());

    const searchText = this._searchText();
    if (searchText?.trim()) {
      params = params.set('search', searchText.trim());
    }

    const status = this._statusFilter();
    if (status?.trim()) {
      params = params.set('status', status);
    }

    const sortField = this._sortField();
    if (sortField?.trim()) {
      params = params.set('sortField', this.mapFieldNameForSorting(sortField));
      params = params.set('sortAscending', this._sortAscending().toString());
    }

    console.log('[DocumentSearchService] HTTP Params:', params.toString());
    return params;
  }

  /**
   * Handle search response with change detection
   */
  private handleSearchResponse(response: PaginatedDocumentResponse): void {
    const newDocuments = response.results ?? [];
    const total = response.total ?? 0;

    // Detect changes
    const currentDocs = this._documents();
    const changes = this.changeDetection.detectChanges(currentDocs, newDocuments);
    
    // Notify changes
    this.changeDetection.notifyChanges(changes);

    // Update state
    this._documents.set(newDocuments);
    this._totalDocuments.set(total);
    this._isLoading.set(false);
  }

  /**
   * Set search text and reset pagination
   */
  setSearchText(text: string): void {
    this._searchText.set(text);
  }

  /**
   * Set status filter
   */
  setStatusFilter(status: string): void {
    this._statusFilter.set(status);
  }

  /**
   * Set sorting parameters
   */
  setSorting(field: string, ascending: boolean): void {
    this._sortField.set(field);
    this._sortAscending.set(ascending);
  }

  /**
   * Clear sorting
   */
  clearSorting(): void {
    this._sortField.set('');
    this._sortAscending.set(true);
  }

  /**
   * Map frontend field names to AODocs API field names for sorting
   */
  private mapFieldNameForSorting(field: string): string {
    const systemFields = ['state', 'title', 'creationDate', 'modificationDate'];
    return systemFields.includes(field) ? `_${field}` : field;
  }

  /**
   * Apply current filters and reset to first page
   * This should be called when library/class selection changes
   */
  applyFilters(): void {
    this._currentPage.set(0);
    this._paginationResetTrigger.update(current => current + 1);
    this.searchDocuments({ 
      page: 0, 
      rows: this._rowsPerPage(), 
      first: 0 
    }, true);
  }

  /**
   * Reset pagination and reload (manual refresh)
   */
  resetAndReload(): void {
    this.applyFilters();
  }

  /**
   * Refresh current page (after save operations)
   */
  refreshCurrentPage(): void {
    if (!this.userAccess.hasValidSelection()) {
      console.warn('[DocumentSearchService] No valid selection, skipping refresh');
      return;
    }

    const currentState: PaginationState = {
      first: this._currentPage() * this._rowsPerPage(),
      rows: this._rowsPerPage(),
      page: this._currentPage()
    };

    console.log('🔄 Refreshing documents after save completion');
    this.searchDocuments(currentState, false);
  }

  /**
   * Reset the service state
   */
  reset(): void {
    this._documents.set([]);
    this._totalDocuments.set(0);
    this._searchText.set('');
    this._statusFilter.set('processed');
    this._sortField.set('');
    this._sortAscending.set(true);
    this._currentPage.set(0);
    this._rowsPerPage.set(10);
    this._paginationResetTrigger.set(0);
    this._isLoading.set(false);
    this._error.set(null);
  }
}